import React from 'react';
import Layout from '../components/layout/Layout';
import type { AuthState } from '../types';
import SEOHead from '../components/SEOHead';

interface TermsProps {
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
}

const Terms: React.FC<TermsProps> = ({ authState, setAuthState }) => {
  return (
    <Layout authState={authState} setAuthState={setAuthState}>
      <SEOHead
        title="Terms and Conditions"
        description="Read the Terms and Conditions for using MarketEye."
        canonicalUrl="/terms"
      />
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms and Conditions</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last updated:</strong> July 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using MarketEye ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                MarketEye is a portfolio management and alert system that allows users to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Connect and manage multiple broker accounts</li>
                <li>Track portfolio holdings and performance</li>
                <li>Set up automated alerts and notifications</li>
                <li>Receive real-time market updates</li>
                <li>Manage trading rules and automation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              <p className="text-gray-700 mb-4">
                To use the Service, you must:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Be at least 18 years old</li>
                <li>Have a valid Google account for authentication</li>
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Subscription and Pricing</h2>
              <p className="text-gray-700 mb-4">
                MarketEye offers both free and paid subscription tiers:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Tier</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Add alerts for up to 50 holdings</li>
                  <li>Alerts will be sent for the first 5 triggers in a month</li>
                  <li>Email notifications only</li>
                </ul>
              </div>
              <div className="bg-blue-50 rounded-lg p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Tier</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Add alerts for up to 200 holdings</li>
                  <li>Alerts will be sent for all triggers in a month</li>
                  <li>Multiple notification channels (Email, WhatsApp)</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data and Privacy</h2>
              <p className="text-gray-700 mb-4">
                We are committed to protecting your privacy and data security:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>All data is encrypted in transit and at rest</li>
                <li>Broker credentials are encrypted and securely stored</li>
                <li>We do not sell or share your personal data with third parties</li>
                <li>You can request deletion of your data at any time</li>
                <li>We comply with applicable data protection regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Investment Disclaimer</h2>
              <p className="text-gray-700 mb-4">
                <strong>Important:</strong> MarketEye is a portfolio management tool and does not provide investment advice. All investment decisions are your responsibility.
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Past performance does not guarantee future results</li>
                <li>Market data may be delayed or inaccurate</li>
                <li>Automated alerts and rules do not constitute investment advice</li>
                <li>Always conduct your own research before making investment decisions</li>
                <li>Consider consulting with a qualified financial advisor</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Service Availability</h2>
              <p className="text-gray-700 mb-4">
                While we strive to maintain high service availability:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>The Service may be temporarily unavailable for maintenance</li>
                <li>Market data feeds may experience delays or interruptions</li>
                <li>We are not responsible for losses due to service interruptions</li>
                <li>We will provide advance notice for scheduled maintenance when possible</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                MarketEye and its operators shall not be liable for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Any investment losses or missed opportunities</li>
                <li>Data accuracy or completeness issues</li>
                <li>Service interruptions or technical failures</li>
                <li>Third-party broker API issues or limitations</li>
                <li>Indirect, incidental, or consequential damages</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Termination</h2>
              <p className="text-gray-700 mb-4">
                You may terminate your account at any time. We may terminate or suspend your account if you:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Violate these terms and conditions</li>
                <li>Engage in fraudulent or illegal activities</li>
                <li>Fail to pay subscription fees (for paid users)</li>
                <li>Abuse the service or other users</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through the Service. Continued use of the Service after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  <strong>Email:</strong> <a href="mailto:ankitdaf@gmail.com" className="text-blue-600 hover:text-blue-700">ankitdaf@gmail.com</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms; 